package ui.report.campaign

import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import common.frontend.api.Io
import io.Repository
import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalTime
import org.ssoft.common.tools.EMPTY
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.await
import tools.printStackTraceInDebug
import tools.toStringResShort

@VM
class ReportCampaignVM : NavigationViewModel() {

    var state by mutableDataState<ReportCampaignScope.Wrapper>()
        private set

    private lateinit var scopeUi: ReportCampaignScope

    override fun onRelease() = Unit

    fun init(reportCampaignScope: ReportCampaignScope) {
        this.scopeUi = reportCampaignScope
        scope.launch {
            initInternal()
        }
    }

    private suspend fun initInternal() {
        if (state is DataState.Idle) {
            state = DataState.Loading()
        }

        scope.launch {
            runCatching {

                val dataJob = async {
                    Repository.api.report.campaignGroup(
                        companyId = scopeUi.args.companyId,
                        campaignGroupId = scopeUi.args.campaignGroupId
                    )
                }

                val impressionJob = async {
                    Repository.api.report.campaignGroupImpression(
                        companyId = scopeUi.args.companyId,
                        campaignGroupId = scopeUi.args.campaignGroupId
                    )
                }

                val data = dataJob.await()
                val oh = buildLocationHours(data)
                val wrapper = ReportCampaignScope.Wrapper(
                    data = data,
                    impression = impressionJob.await(),
                    oh = oh
                )
                wrapper.chart.rebuild()
                state = DataState.Result(wrapper)
            }.onFailure {
                it.printStackTraceInDebug()
                state = DataState.Error(it.message!!, throwable = it)
            }
        }
    }

    private suspend fun buildLocationHours(data: Io.ReportCampaign): MutableList<ReportCampaignScope.LocationOhPreview> {

        Res.string.global__day_of_week__short_mon.await()
        Res.string.global__day_of_week__short_tue.await()
        Res.string.global__day_of_week__short_wen.await()
        Res.string.global__day_of_week__short_thu.await()
        Res.string.global__day_of_week__short_fri.await()
        Res.string.global__day_of_week__short_sat.await()
        Res.string.global__day_of_week__short_sun.await()


        val MIDNIGHT = LocalTime(
            hour = 23,
            minute = 59,
            second = 59,
            nanosecond = 999
        )

        val result = mutableListOf<ReportCampaignScope.LocationOhPreview>()
        data.locations
            .forEach { location ->
                val days = buildList {
                    location.oh
                        .sortedBy { it.day }
                        .forEachIndexed { index, day ->
                            val dayBefore = location.oh.getOrElse(index - 1) { location.oh.last() }

                            if (dayBefore.enabled == 1 && dayBefore.start > dayBefore.end) {
                                add(
                                    Io.ImplLocation.Day(
                                        enabled = 1,
                                        day = day.day,
                                        start = LocalTime.EMPTY,
                                        end = dayBefore.end
                                    )
                                )
                            }
                            if (day.enabled == 1) {
                                add(
                                    Io.ImplLocation.Day(
                                        enabled = 1,
                                        day = day.day,
                                        start = day.start,
                                        end =
                                            if (day.start > day.end) MIDNIGHT
                                            else day.end
                                    )
                                )
                            }
                        }
                }

                class OhEntry(
                    var days: MutableSet<String> = mutableSetOf(),
                    var hours: MutableSet<String> = mutableSetOf()
                )

                val stack = mutableListOf<OhEntry>()
                days.forEach { dayEntry ->
                    val dow = api.DayOfWeek[dayEntry.day].toStringResShort().await()
                    fun Int.pad() = toString().padStart(2, '0')
                    val dur = "(${dayEntry.start.hour.pad()}:${dayEntry.start.minute.pad()} - ${dayEntry.end.hour.pad()}:${dayEntry.end.minute.pad()})"
                    stack.firstOrNull { stackEntry -> stackEntry.days.contains(dow) }
                        ?.also { stackEntry -> stackEntry.hours += dur }
                        ?: run {
                            stack += OhEntry(
                                days = mutableSetOf(dow),
                                hours = mutableSetOf(dur),
                            )
                        }
                }

                val merge = mutableListOf<OhEntry>()
                stack.forEach { entry ->
                    val hMerge = entry.hours.joinToString()
                    merge.firstOrNull { it.hours.joinToString() == hMerge }
                        ?.also { it.days += entry.days.first() }
                        ?: run { merge += entry }
                }
                result += ReportCampaignScope.LocationOhPreview(
                    id = location.id,
                    name = location.name,
                    stack = merge.map {
                        it.days.joinToString() to it.hours.joinToString()
                    }
                )
            }
        return result
    }
}
